import { PostHogProvider } from '@posthog/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { UILayersProvider } from '@tractian/react-ui-context'
import { Tooltip } from '@tractian/ui'
import { I18nextProvider } from 'react-i18next'
import { RouterProvider } from 'react-router'
import { Toaster } from 'sonner'

import { createI18n } from '@/app/i18n'
import { MapsScript } from '@/app/maps-script'
import { createQueryClient } from '@/app/queryClient'
import { Sentry } from '@/app/sentry'
import { VersionCheck } from '@/app/version-check'
import { AuthProvider } from '@/lib/authentication/auth-provider'
import { DevTools } from '@/features/dev-tools/containers/dev-tools'
import { TractianUserProvider } from '@/lib/authentication/tractian-user-context'
import { TractianUIProvider } from '@/lib/i18n/tractian-ui-provider'
import { posthog, postHogService } from '@/lib/posthog'
import { publicRouter, router } from '@/routes/router'

const queryClient = createQueryClient()
const i18n = createI18n()

postHogService.init()

function AppFallback() {
  return (
    <main className="flex h-full flex-col items-center justify-center gap-2 text-center">
      <h1 className="text-2xl font-semibold text-gray-900">Something went wrong</h1>
      <p className="text-gray-500">The application failed to load. Please refresh the page.</p>
    </main>
  )
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={<AppFallback />}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <AuthProvider>
            <PostHogProvider client={posthog}>
              <TractianUserProvider>
                <TractianUIProvider>
                  <UILayersProvider>
                    <Tooltip.Provider>
                      <MapsScript>
                        <RouterProvider router={router} />
                      </MapsScript>
                    </Tooltip.Provider>
                    {/* Global dev-tools modal (Cmd+D) — the kit Modal needs UILayers context. */}
                    <DevTools />
                  </UILayersProvider>
                </TractianUIProvider>
              </TractianUserProvider>
            </PostHogProvider>
          </AuthProvider>
          {/*
            Anonymous public router tree (ADR 006) - a sibling OUTSIDE
            `AuthProvider` so an unauthenticated visitor reaching
            `/document/:docId` is never redirected to login. It shares the
            QueryClient + i18n providers so `usePublicDocumentUrl` and
            `useTranslation` work, but no auth/user/maps providers. Each
            `RouterProvider` renders nothing for URLs its own routes do not
            match, so the two trees coexist without conflict.
          */}
          <RouterProvider router={publicRouter} />
          <VersionCheck />
          <Toaster richColors position="top-right" />
        </I18nextProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}
